import React, { useState } from "react";
import { format, parseISO } from "date-fns";
import { PortalBookingStep1 } from "./portal_booking_step_1";
import { PortalBookingStep2 } from "./portal_booking_step_2";
import {
  useCreateMultiPassBooking,
  invalidatePartnerAccessCache,
  Partner,
  usePartnerCatalogsQuery,
  MultiPassAccess,
} from "../../api";
import { toDay } from "../../day_utils";
import { PortalBookingStep3 } from "./portal_booking_step_3";

import { UsedCountDetailsCard } from "../../portal/components/used_count_details_card";
import { useMultiAccessesQuery } from "../../api";
import { PortalModal } from "./portal_modal";
import styles from "./multipass_card_list.module.css";
import { useMultiPassOrderHelpers } from "../../order_params";
import { BookingType } from "../../../data";
import { PortalEmptyState } from "./portal_empty_state";
import { PortalButton } from "./portal_button";
import { toMultiPassAccess } from "../../../fm/data/orders";
import { useAnalytics } from "@/lib/analytics/analytics";
import { useToast } from "../../../components";
import {
  DateRangePicker,
  PickingRange,
} from "../../../offering_v2/components/date_range_picker/date_range_picker";

export enum PassTabs {
  ActivePasses = "Active Passes",
  ExpiredPasses = "Expired Passes",
}

interface MultiPassCardListProps {
  partner: Partner;
  filterBy: PassTabs;
}

export function MultiPassCardList({
  partner,
  filterBy,
}: MultiPassCardListProps) {
  const analytics = useAnalytics();
  const toast = useToast();

  const [openDialog, setOpenDialog] = useState(false);
  const [multipassId, setMultipassId] = useState<string>();
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [currentStep, setCurrentStep] = useState(0);
  const [isCreatingNewBooking, setIsCreatingNewBooking] = useState(false);

  const { data: multiPassesData, mutate } = useMultiAccessesQuery(partner.id, {
    suspense: false,
  });
  const multiPassAccesses: MultiPassAccess[] =
    multiPassesData?.map(toMultiPassAccess) || [];
  const { availableDays, loading, error } = useMultiPassOrderHelpers(
    partner.id,
    multipassId
  );
  const createMultiPassBooking = useCreateMultiPassBooking();
  const catalogs = usePartnerCatalogsQuery(partner.partnerSlug, {
    suspense: false,
  });

  const selectedPass = multiPassAccesses.find((a) => a.id === multipassId);

  let dayPassSlug: string | undefined;
  if (catalogs) {
    for (const catalog of catalogs) {
      for (const category of catalog.partnerCatalogCategories) {
        const { catalogOfferings: offerings = [] } = category;

        for (const offering of offerings) {
          if (offering.multiPassOffering) {
            dayPassSlug = offering.slug;
          }
        }
      }
    }
  }
  const offeringHref = `/offerings/${dayPassSlug}`;

  const filteredAccesses = multiPassAccesses.filter((a) =>
    filterBy === PassTabs.ExpiredPasses ? a.expired : !a.expired
  );

  if (!filteredAccesses.length) {
    if (filterBy === PassTabs.ExpiredPasses) {
      return (
        <PortalEmptyState
          title="There are no expired passes"
          description="When your multi passes expired, they will appear here."
        />
      );
    }

    return (
      <PortalEmptyState
        title="There are no active passes"
        description="When you purchase multi passes, they will appear here."
        link={
          dayPassSlug
            ? {
                href: offeringHref,
                text: "Visit day pass page",
              }
            : undefined
        }
      />
    );
  }

  const handleClickBooking = (multipassId: string) => {
    setOpenDialog(true);
    setMultipassId(multipassId);

    const openedPass = multiPassAccesses.find((a) => a.id === multipassId);
    analytics.event("Open Day Pass Booking Modal", {
      "Used Count": openedPass?.bookingsUsedCount,
      "Remaining Count": openedPass?.bookingsUnusedCount,
      "Multi Pass UUID": multipassId,
      "Space UUID": openedPass?.space.id,
      "Space Name": openedPass?.space.name,
      Category: "MultiPass",
      Source: "Portal",
    });
  };

  const handleConfirmBooking = async () => {
    if (!multipassId || !selectedDate) {
      return;
    }

    setIsCreatingNewBooking(true);
    try {
      await createMultiPassBooking(partner.id, multipassId, {
        date: toDay(selectedDate),
      });
    } catch (error) {
      toast.error({
        message:
          (error instanceof Error && error.message) ||
          "Fail to confirm booking. Please try later or contact support.",
      });
      return;
    } finally {
      setIsCreatingNewBooking(false);
    }

    const multiPasses = [...multiPassesData];
    const index = multiPasses.findIndex((mP) => mP.id === multipassId);
    if (index !== -1) {
      const pass = { ...multiPasses[index] };
      pass.bookings_used_count++;
      pass.bookings_unused_count--;
      multiPasses[index] = pass;
      mutate(multiPasses);
    }
    setCurrentStep(2);
    analytics.event("Confirm Book Multi Pass Day", {
      "Multi Pass UUID": multipassId,
      "Space UUID": selectedPass?.space.name,
      "Space Name": selectedPass?.space.name,
      Category: "MultiPass",
      Source: "Portal",
    });
  };

  const handleCloseDialog = async () => {
    setOpenDialog(false);
    setMultipassId(undefined);
    setSelectedDate(undefined);
    setCurrentStep(0);
    if (currentStep === 2) {
      await invalidatePartnerAccessCache(partner.id);
    }
  };

  const handleContinueToConfirmBooking = () => {
    analytics.event("Continue To Confirm Booking", {
      "Used Count": selectedPass?.bookingsUsedCount,
      "Remaining Count": selectedPass?.bookingsUnusedCount,
      "Multi Pass UUID": multipassId,
      "Space UUID": selectedPass?.space.id,
      "Space Name": selectedPass?.space.name,
      Category: "MultiPass",
      Source: "Portal",
    });
    setCurrentStep(1);
  };

  const handleClickBackToSelectDate = () => {
    analytics.event("Back To Select Date", {
      "Used Count": selectedPass?.bookingsUsedCount,
      "Remaining Count": selectedPass?.bookingsUnusedCount,
      "Multi Pass UUID": multipassId,
      "Space UUID": selectedPass?.space.id,
      "Space Name": selectedPass?.space.name,
      Category: "MultiPass",
      Source: "Portal",
    });
    setCurrentStep(0);
  };

  const formatedBookingDate =
    selectedDate && format(selectedDate, "MMMM dd, yyyy");

  const steps = selectedPass && [
    {
      title: "Select a Date",
      content: (
        <PortalBookingStep1
          title={selectedPass.location.name}
          warningMessage=""
          usedCount={selectedPass.bookingsUsedCount}
          totalCount={selectedPass.numberOfEntries}
          selectedCount={selectedDate ? 1 : 0}
          rightContent={
            error ? (
              <div>Sorry! Error happened while loading the calendar.</div>
            ) : (
              <div className="border rounded">
                <DateRangePicker
                  value={{ startDate: selectedDate }}
                  availableDates={
                    availableDays?.map((d) => parseISO(d.date)) || []
                  }
                  pickingRange={PickingRange.OneDate}
                  onChange={(value) => setSelectedDate(value.startDate)}
                  isLoading={loading}
                />
              </div>
            )
          }
        />
      ),
      footer: (
        <div className={styles.footerWrapper}>
          <PortalButton
            onClick={handleContinueToConfirmBooking}
            disabled={!selectedDate}
          >
            Continue
          </PortalButton>
        </div>
      ),
    },
    {
      title: "Confirm Booking",
      content: (
        <PortalBookingStep2
          spaceName={selectedPass.location.name}
          imgUrl={selectedPass.space.thumbnailImage}
          location={selectedPass.location.streetAddress}
          usedCount={1}
          remainingCount={
            selectedPass.numberOfEntries - (selectedPass.bookingsUsedCount + 1)
          }
          bookingDate={formatedBookingDate}
          bookingType={BookingType.Daily}
          capacity={1}
          cancelPolicy="Cancellable before booking date"
        />
      ),
      footer: (
        <PortalButton
          onClick={handleConfirmBooking}
          disabled={isCreatingNewBooking}
        >
          {isCreatingNewBooking ? "Loading..." : "Confirm booking"}
        </PortalButton>
      ),
    },
    {
      title: "Booking Confirmation",
      content: (
        <PortalBookingStep3
          spaceName={selectedPass.location.name}
          imgUrl={selectedPass.space.thumbnailImage}
          location={selectedPass.location.streetAddress}
          usedCount={selectedPass.bookingsUsedCount}
          remainingCount={selectedPass.bookingsUnusedCount}
          bookingDate={formatedBookingDate}
          bookingEmail={selectedPass.order.email}
          bookingType={BookingType.Daily}
          capacity={1}
        />
      ),
      footer: <PortalButton onClick={handleCloseDialog}>Close</PortalButton>,
    },
  ];

  return (
    <div>
      <div className={styles.multiPassCardList}>
        {filteredAccesses.map((mpa) => (
          <div key={mpa.id} className={styles.multiPassCardWrapper}>
            <UsedCountDetailsCard
              title={mpa.name}
              description={mpa.location.streetAddress}
              usedCount={mpa.bookingsUsedCount}
              totalCount={mpa.numberOfEntries}
              isExpired={mpa.expired}
              expiredDate={format(parseISO(mpa.expirationDate), "MMM dd, yyyy")}
              onClickBook={() => handleClickBooking(mpa.id)}
              bookingDisabled={mpa.bookingsUsedCount === mpa.numberOfEntries}
              bookingButtonText="Book a pass"
            />
          </div>
        ))}
      </div>

      {steps && (
        <PortalModal
          show={openDialog}
          onHide={handleCloseDialog}
          title={steps[currentStep].title}
          body={steps[currentStep].content}
          footer={steps[currentStep].footer}
          onClickBackButton={
            currentStep === 1 ? handleClickBackToSelectDate : undefined
          }
        />
      )}
    </div>
  );
}
